/**
 * СКАЧИВАНИЕ ВЛОЖЕНИЙ В ОБЫЧНОМ БРАУЗЕРЕ (Антон, 2026-11-04 20:14:
 * «Сделай так, чтоб работало в любом браузере»).
 *
 * ПОВОД: в Opera GX 138 (Windows, Chromium) путь через service worker
 * (/__alg-download/<id>, см. serviceWorkerDownload.ts и public/sw.js) не
 * срабатывал: вместо файла пользователь видел «какой-то JSON», а запросов
 * /__alg-download на сервере не было ни одного.
 *
 * ЧТО ДЕЛАЕМ В ОБЫЧНОМ БРАУЗЕРЕ: файл уже скачан с Authorization: Bearer
 * (fetchAuthedBlob из matrix/media.ts, тот же путь, что для показа картинок).
 * Из этого Blob делаем blob:-URL, кликаем временную <a download="имя">
 * БЕЗ target=_blank (новая вкладка не открывается, service worker не нужен)
 * и освобождаем URL с задержкой: часть браузеров (Firefox, Safari) читает
 * blob уже после click().
 *
 * ОБЁРТКИ НЕ ТРОГАЕМ. Mac-приложение (WKWebView) — там blob-скачивание
 * не работало (16.09), и оболочка перехватывает a[download] с
 * /__alg-download/. Android (Capacitor) и Electron-оболочка тоже идут
 * прежним путём. isPlainBrowser() возвращает false для любой из них, и
 * вызывающий код выполняет СТАРЫЙ код без изменений.
 */
import type { MatrixClient } from "matrix-js-sdk";
import { tr } from "../i18n";
import { fetchAuthedBlob, MediaHttpError } from "../matrix/media";

/** Сколько держать blob:-URL после click(): браузер мог ещё не начать чтение. */
const REVOKE_DELAY_MS = 60_000;

type WrapperWindow = Window & {
  ALGDesktop?: unknown;
  webkit?: { messageHandlers?: Record<string, unknown> };
  Capacitor?: { isNativePlatform?: () => boolean };
};

/**
 * true — страница открыта в обычном браузере (Safari, Chrome, Opera,
 * Edge, Firefox, Яндекс…). false — внутри любой нашей оболочки:
 *  - Mac-приложение (alg-messenger-mac-native, WKWebView): мост
 *    window.ALGDesktop + window.webkit.messageHandlers.ALGSaveFile,
 *    в UA метка «algsuccessMac/»;
 *  - Electron (alg-messenger-desktop): window.ALGDesktop из preload,
 *    в UA метка «ALGMessengerDesktop/»;
 *  - Android (Capacitor): window.Capacitor.isNativePlatform() === true;
 *    на всякий случай и Android System WebView по маркеру «; wv)» в UA.
 * Любой признак обёртки → false: при сомнении выбирается прежний путь.
 */
export function isPlainBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as WrapperWindow;
  try {
    if (w.ALGDesktop !== undefined) return false;
    if (w.webkit?.messageHandlers?.ALGSaveFile) return false;
    if (w.Capacitor?.isNativePlatform?.() === true) return false;
  } catch {
    return false;
  }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/algsuccessMac\/|ALGMessengerDesktop\//.test(ua)) return false;
  if (/Android/.test(ua) && /; wv\)/.test(ua)) return false;
  return true;
}

function safeFilename(value: string): string {
  const cleaned = (value || "")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]/g, "_")
    .trim()
    .slice(0, 180);
  return cleaned && cleaned !== "." && cleaned !== ".." ? cleaned : "download";
}

/** Отдать готовый Blob браузеру как файл. Новую вкладку не открывает. */
export function saveBlobInPage(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = safeFilename(filename);
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
  }
}

export type BrowserDownloadSource = {
  filename: string;
  /** Blob, уже скачанный с авторизацией (лайтбокс): повторно не качаем. */
  blob?: Blob | null;
  /** blob:-URL из useAuthedMedia (лента, панели файлов). */
  objectUrl?: string | null;
  /** Запасной путь: заново скачать по mxc с Authorization: Bearer. */
  client?: MatrixClient | null;
  mxc?: string | null;
};

async function blobFromLocalUrl(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`local blob ${response.status}`);
  return response.blob();
}

/**
 * Скачать вложение в обычном браузере. Источник по порядку: готовый Blob →
 * blob:-URL превью → свежий авторизованный fetch по mxc. Ошибка пробрасывается;
 * текст для человека даёт describeDownloadError().
 */
export async function downloadInBrowser(source: BrowserDownloadSource): Promise<void> {
  let blob: Blob | null = source.blob ?? null;
  if (!blob && source.objectUrl) {
    try {
      blob = await blobFromLocalUrl(source.objectUrl);
    } catch (localFailure) {
      // blob:-URL могли уже освободить (компонент перерисовался) — качаем заново.
      if (!(source.client && source.mxc)) throw localFailure;
    }
  }
  if (!blob) {
    if (!source.client || !source.mxc) throw new Error("no download source");
    blob = await fetchAuthedBlob(source.client, source.mxc);
  }
  saveBlobInPage(blob, source.filename);
}

/** Понятный текст ошибки скачивания (для тоста/плашки), а не технический. */
export function describeDownloadError(failure: unknown): string {
  if (failure instanceof MediaHttpError) {
    if (failure.status === 401 || failure.status === 403) return tr("download.errorAuth");
    if (failure.status === 404) return tr("download.errorNotFound");
    return `${tr("download.errorGeneric")} (${failure.status})`;
  }
  if (failure instanceof TypeError) return tr("download.errorNetwork");
  return tr("download.errorGeneric");
}
